import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  LinearProgress,
  Typography,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyResultsModal from "./KeyResultsModal";
import {
  statusStyles,
  formatDate,
  truncateTarget,
  getProgressColor,
  formatProgress,
} from "./format";
import { useNavigate } from "react-router-dom";

const OKRList = ({ title, objectives, loading, fetchChildObjectives }) => {
  const navigate = useNavigate();
  // const [objectives, setObjectives] = useState([]);
  const [childObjectives, setChildObjectives] = useState({});
  // const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState(null);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     setLoading(true);
  //     try {
  //       const data = await fetchParentObjectives();
  //       setObjectives(data);
  //     } catch (error) {
  //       console.error(`Failed to fetch parent objectives for ${title}:`, error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchData();
  // }, [fetchParentObjectives, title]);

  const toggleExpand = async (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

    if (!childObjectives[id]) {
      try {
        const children = await fetchChildObjectives(id);
        setChildObjectives((prev) => ({ ...prev, [id]: children }));
      } catch (error) {
        console.error(`Failed to fetch child objectives for ID: ${id}`, error);
      }
    }
  };

  const handleOpenModal = (objectiveId) => {
    setSelectedObjectiveId(objectiveId);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleOpenDetailPage = (objectiveId) => {
    navigate(`/okr-detail/${objectiveId}`);
  };

  const renderObjectiveRow = (okr, level = 0) => {
    const paddingLeft = `${level * 30}px`;

    return (
      <React.Fragment key={okr.Id}>
        <TableRow>
          <TableCell style={{ paddingLeft }}>
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => toggleExpand(okr.Id)}>
                {expanded[okr.Id] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
              <Avatar
                alt={okr.User?.FirstName || "Anonymous"}
                src={
                  okr.User?.Avatar
                    ? `data:image/png;base64,${okr.User.Avatar}`
                    : "/default-avatar.png"
                }
                sx={{ width: 40, height: 40, mr: 2 }}
              />
              <strong
                onClick={() => handleOpenDetailPage(okr.Id)}
                style={{ cursor: "pointer" }}
              >
                {truncateTarget(okr.Target)}
              </strong>
            </Box>
          </TableCell>
          <TableCell>
            {okr.User?.FirstName} {okr.User?.LastName || "Anonymous"}
          </TableCell>
          <TableCell>
            <Box display="flex" alignItems="center">
              <Box width="100%" mr={1}>
                {/* <LinearProgress
                  variant="determinate"
                  value={okr.ProgressPercentage || 0}
                  sx={{
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: getProgressColor(
                        okr.ProgressPercentage || 0
                      ),
                    },
                  }}
                /> */}
                <LinearProgress
                  variant="determinate"
                  value={Math.min(okr.ProgressPercentage || 0, 100)} // Hiển thị tối đa 100%
                  sx={{
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: getProgressColor(
                        Math.min(okr.ProgressPercentage || 0, 100)
                      ),
                    },
                  }}
                />
              </Box>
              <Typography variant="body2" color="textSecondary">
                {`${formatProgress(okr.ProgressPercentage || 0)}%`}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Button
              variant="outlined"
              sx={{ padding: "2px 4px" }}
              onClick={() => handleOpenModal(okr.Id)}
            >
              {okr.KeyResults?.length || 0} Key Results
            </Button>
          </TableCell>
          <TableCell>{formatDate(okr.StartDate)}</TableCell>
          <TableCell>{formatDate(okr.EndDate)}</TableCell>
          <TableCell>
            <Typography
              variant="body2"
              sx={{
                ...statusStyles[okr.ProcessStatus],
              }}
            >
              {okr.ProcessStatus.replace("_", " ")}
            </Typography>
          </TableCell>
        </TableRow>

        {expanded[okr.Id] &&
          childObjectives[okr.Id]?.map((childOkr) =>
            renderObjectiveRow(childOkr, level + 1)
          )}
      </React.Fragment>
    );
  };

  return (
    <div>
      <h2>{title}</h2>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "500px",
          overflowY: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: "40%", fontWeight: "bold" }}>
                Target
              </TableCell>
              <TableCell style={{ width: "10%", fontWeight: "bold" }}>
                Creator
              </TableCell>
              <TableCell style={{ width: "10%", fontWeight: "bold" }}>
                Progress
              </TableCell>
              <TableCell style={{ width: "10%", fontWeight: "bold" }}>
                Key Results
              </TableCell>
              <TableCell style={{ width: "10%", fontWeight: "bold" }}>
                Start Date
              </TableCell>
              <TableCell style={{ width: "10%", fontWeight: "bold" }}>
                End Date
              </TableCell>
              <TableCell style={{ width: "10%", fontWeight: "bold" }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={25} />
                </TableCell>
              </TableRow>
            ) : objectives.length > 0 ? (
              objectives.map((okr) => renderObjectiveRow(okr))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No {title} available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <KeyResultsModal
        open={openModal}
        onClose={handleCloseModal}
        objectiveId={selectedObjectiveId}
      />
    </div>
  );
};

export default OKRList;
